import React, { useState, useEffect, useCallback } from 'react';
import { Anchor, Text, Group, Tooltip } from '@mantine/core';
import { EntityCategorizationService } from '../../services/EntityCategorizationService';

const LOOSE_REF_WARNING = 'Loose reference; target may no longer exist.';

interface TargetIdLinkProps {
    targetId: string;
    targetType: string;
    /** When false, render as plain text (type not in tier1 entitlements). */
    canNavigateToType: boolean;
    /** Optional display name for the target (e.g. TargetDisplayName); shown as link text when present. */
    targetDisplayName?: string;
}

/**
 * Renders TargetId as a link to the entity view when TargetType is in tier1 entitlements.
 * Shows an asterisk with tooltip that this is a loose reference and the target may no longer exist.
 */
export const TargetIdLink: React.FC<TargetIdLinkProps> = ({
    targetId,
    targetType,
    canNavigateToType,
    targetDisplayName
}) => {
    const linkLabel = (targetDisplayName ?? '').trim() || targetId;
    const [route, setRoute] = useState<string | null>(null);
    const [loading, setLoading] = useState(!!targetType);

    useEffect(() => {
        if (!targetType || !targetId || !canNavigateToType) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        const resolveRoute = async () => {
            try {
                const normalizedType = String(targetType).trim().toLowerCase();
                const categorizationService = EntityCategorizationService.getInstance();
                const category = await categorizationService.getEntityCategory(normalizedType);

                if (cancelled) return;

                let resolvedRoute: string;
                if (normalizedType === 'group') {
                    resolvedRoute = `#groups/view/${targetId}`;
                } else if (normalizedType === 'class') {
                    resolvedRoute = `#classes/view/${targetId}`;
                } else if (['school', 'building', 'responsibilitycenter'].includes(normalizedType)) {
                    resolvedRoute = `#localities/${normalizedType}/view/${targetId}`;
                } else if (category.type === 'person') {
                    resolvedRoute = `#Person/${normalizedType}/view/${targetId}`;
                } else if (category.type === 'locality') {
                    resolvedRoute = `#localities/${normalizedType}/view/${targetId}`;
                } else if (category.type === 'admin') {
                    resolvedRoute = `#admin/${normalizedType}/view/${targetId}`;
                } else {
                    resolvedRoute = `#other/${normalizedType}/view/${targetId}`;
                }
                setRoute(resolvedRoute);
            } catch {
                if (!cancelled) setRoute(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        resolveRoute();
        return () => { cancelled = true; };
    }, [targetId, targetType, canNavigateToType]);

    const handleClick = useCallback((e: React.MouseEvent) => {
        if (!route) return;
        e.preventDefault();
        if (e.button === 1) {
            const fullUrl = typeof window !== 'undefined'
                ? `${window.location.origin}${window.location.pathname}${window.location.search || ''}${route}`
                : route;
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        window.location.hash = route;
    }, [route]);

    const debugProps = {
        'data-target-id': targetId,
        'data-target-type': targetType,
        'data-target-id-can-navigate': canNavigateToType,
        'data-target-id-route': route ?? undefined,
        'data-target-id-loading': loading
    };

    if (!canNavigateToType) {
        return (
            <Text component="span" style={{ wordBreak: 'break-word' }} {...debugProps}>
                {targetId}
            </Text>
        );
    }

    if (loading || !route) {
        return (
            <Group gap={4} align="center" wrap="nowrap" {...debugProps}>
                <Text component="span" style={{ wordBreak: 'break-word' }}>{linkLabel}</Text>
                <Tooltip label={LOOSE_REF_WARNING}>
                    <Text component="span" c="dimmed" style={{ cursor: 'help' }}>*</Text>
                </Tooltip>
            </Group>
        );
    }

    return (
        <Group gap={4} align="center" wrap="nowrap" {...debugProps}>
            <Anchor href={route} onClick={handleClick} underline="hover" style={{ wordBreak: 'break-word' }} title={linkLabel !== targetId ? targetId : undefined}>
                {linkLabel}
            </Anchor>
            <Tooltip label={LOOSE_REF_WARNING}>
                <Text component="span" c="dimmed" style={{ cursor: 'help' }}>*</Text>
            </Tooltip>
        </Group>
    );
};
